import * as React from 'react';

import type * as api from '@gitbook/api-client';
import {
    Button,
    LoadingIndicator,
    PageSplitDescription,
    PageSplitTitle,
} from '@gitbook/spine-react';

import { OrganizationIcon } from '../../Components';
import { useSetAnalyticsScreen } from '../../navigation';
import { LoginScreenLayout } from './LoginScreenLayout';
import { SSOProviderLoginList } from './SSOProviderLoginList';

/**
 * Login screen for an organization with SSO.
 */
export function SSOOrganizationLogin(props: {
    loggedIn: boolean;
    organization: api.Organization;
    onUseNormalLogin: () => void;
    onBack?: () => void;
}): React.ReactElement {
    const { organization, onUseNormalLogin, loggedIn, onBack } = props;

    useSetAnalyticsScreen('login_sso');

    return (
        <LoginScreenLayout onClose={onBack}>
            {organization.sso && loggedIn ? (
                <SignInWithSSO organization={organization} />
            ) : (
                <SignInWithWorkEmail
                    organization={organization}
                    onUseNormalLogin={onUseNormalLogin}
                />
            )}
        </LoginScreenLayout>
    );
}

function SignInWithSSO(props: { organization: api.Organization }) {
    const { organization } = props;
    return (
        <>
            <PageSplitTitle logo={<OrganizationIcon organization={organization} size={48} />}>
                Sign in with SSO
            </PageSplitTitle>
            <PageSplitDescription>
                <div className="flex flex-col gap-4">
                    <div>
                        To access GitBook, you're required to sign in to the{' '}
                        <strong>{organization.title}</strong> organization via its SSO provider.
                    </div>
                    <div>This will link SSO to your existing account.</div>
                </div>
            </PageSplitDescription>
            <React.Suspense fallback={<LoadingIndicator />}>
                <SSOProviderLoginList organization={organization} />
            </React.Suspense>
        </>
    );
}

function SignInWithWorkEmail(props: {
    organization: api.Organization;
    onUseNormalLogin: () => void;
}) {
    const { organization, onUseNormalLogin } = props;
    return (
        <>
            <PageSplitTitle logo={<OrganizationIcon organization={organization} size={48} />}>
                Sign in with your work email
            </PageSplitTitle>
            <PageSplitDescription>
                <div className="flex flex-col gap-4">
                    <div>We have found the following methods to sign in using your work email.</div>
                    <div>
                        Sign-in to the <strong>{organization.title}</strong> organization using SSO.
                    </div>
                </div>
            </PageSplitDescription>

            <React.Suspense fallback={<LoadingIndicator />}>
                <SSOProviderLoginList organization={organization} />
            </React.Suspense>

            {!organization.sso && (
                <Button
                    kind="secondary"
                    onPress={onUseNormalLogin}
                    size="large"
                    label="I have a guest account"
                />
            )}
        </>
    );
}
